import {
	ErrorCode as WsclientErrorCode,
	FaultInfo,
	Result,
	ResultException,
	WebserviceException
} from "@softvision/webpdf-wsclient-9-typescript/lib/exception";
import ErrorCode from "./ErrorCode";
import {
	ConfigurationResult,
	ExecutableResult,
	WebserviceException as WebserviceExceptionBean,
	WebserviceResult,
	WebserviceResultStackTrace
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import FunctionHelper from "../helper/FunctionHelper";
import {AxiosError} from "axios";

export default class Exception extends Error {
	private _code?: ErrorCode | number;
	private _exitCode?: ErrorCode | number;
	private _httpStatus?: number;
	private _httpStatusText?: string;
	private _originalException: any;
	private _documentId?: string;
	private _executableError?: string;
	private _executableCommandline?: string;

	/**
	 * Return the code.
	 */
	public get code(): ErrorCode | number | undefined {
		return this._code;
	}

	/**
	 * Set the code.
	 */
	public set code(value: ErrorCode | number | undefined) {
		this._code = value;
	}

	/**
	 * Return the executeable code.
	 */
	public get exitCode(): ErrorCode | number | undefined {
		return this._exitCode;
	}

	/**
	 * Set the executeable code.
	 */
	public set exitCode(value: ErrorCode | number | undefined) {
		this._exitCode = value;
	}

	/**
	 * Return the httpStatus.
	 */
	get httpStatus(): number | undefined {
		return this._httpStatus;
	}

	/**
	 * Set the httpStatus.
	 */
	set httpStatus(value: number | undefined) {
		this._httpStatus = value;
	}

	/**
	 * Return the httpStatusText.
	 */
	get httpStatusText(): string | undefined {
		return this._httpStatusText;
	}

	/**
	 * Set the httpStatusText.
	 */
	set httpStatusText(value: string | undefined) {
		this._httpStatusText = value;
	}

	/**
	 * Return the original exception.
	 */
	get originalException(): any {
		return this._originalException;
	}

	/**
	 * Set the original exception.
	 */
	set originalException(value: any) {
		this._originalException = value;
	}

	get documentId(): string | undefined {
		return this._documentId;
	}

	set documentId(value: string | undefined) {
		this._documentId = value;
	}

	get executableError(): string | undefined {
		return this._executableError;
	}

	set executableError(value: string | undefined) {
		this._executableError = value;
	}

	get executableCommandline(): string | undefined {
		return this._executableCommandline;
	}

	set executableCommandline(value: string | undefined) {
		this._executableCommandline = value;
	}

	public static parseAdminResult(result: ConfigurationResult): Exception {
		let portalException: Exception = new Exception();

		if (FunctionHelper.exists(result.executableError) && FunctionHelper.applyValue(
			result.executableError!.message, ExecutableResult.getMessageDefault()
		) !== "") {
			portalException.message = FunctionHelper.applyValue(
				result.executableError?.message, ExecutableResult.getMessageDefault()
			);
			portalException.executableError = FunctionHelper.applyValue(
				result.executableError?.consoleOutput, ExecutableResult.getConsoleOutputDefault()
			).join("\n");
			portalException.executableCommandline = FunctionHelper.applyValue(
				result.executableError?.commandLine, ExecutableResult.getCommandLineDefault()
			).join(" ");
		} else {
			portalException.message = FunctionHelper.applyValue(
				result.error!.description, WebserviceResult.getDescriptionDefault()
			)
		}

		portalException.originalException = result;
		portalException.code = result.error?.code;
		portalException.exitCode = result.executableError?.exitCode;

		let stacktrace: string = "";
		for (let entry of FunctionHelper.applyValue(
			result.error?.stackTrace?.stackTrace, WebserviceResultStackTrace.getStackTraceDefault()
		)) {
			stacktrace +=
				entry.className + "." + entry.methodName + "(" + entry.fileName + ":" + entry.lineNumber + ")\n";
		}

		portalException.stack = stacktrace;

		return portalException;
	}

	public static parseException(exception: Error): Exception {
		let portalException: Exception;

		if (exception.name === "AxiosError") {
			portalException = new Exception();
			portalException.name = FunctionHelper.applyValue((exception as AxiosError).code, exception.name);
			portalException.message = exception.message;
			portalException.stack = exception.stack;
		} else if (exception instanceof ResultException) {
			portalException = this.parseResultException(exception);
		} else if (exception instanceof Exception) {
			portalException = exception;
		} else {
			portalException = new Exception();
			portalException.originalException = exception;
			portalException.message = exception.message;
			portalException.stack = exception.stack;
		}

		return portalException;
	}

	/**
	 * translate ExceptionBean to PortalException
	 */
	public static parseExceptionBean(exception: WebserviceExceptionBean): Exception {
		let portalException: Exception = new Exception();
		portalException.originalException = exception;
		portalException.code = FunctionHelper.applyValue(
			exception.errorCode, WebserviceExceptionBean.getErrorCodeDefault()
		);
		portalException.message = FunctionHelper.applyValue(
			exception.errorMessage, WebserviceExceptionBean.getErrorMessageDefault()
		);
		portalException.stack = FunctionHelper.applyValue(
			exception.stackTrace, WebserviceExceptionBean.getStackTraceDefault()
		);
		return portalException;
	}

	/**
	 * translate ResultException to PortalException
	 */
	public static parseResultException(exception: ResultException): Exception {
		let portalException: Exception = new Exception();

		portalException.originalException = exception;

		let result: Result | undefined = exception.result;
		if (!FunctionHelper.exists(result)) {
			portalException.message = exception.message;
			return portalException;
		}

		let errorCode: WsclientErrorCode | undefined = result.error;
		if (FunctionHelper.exists(errorCode)) {
			portalException.code = errorCode.code;
			portalException.message = errorCode.message;
		}

		let innerException: ResultException | undefined = result.exception as ResultException;
		if (FunctionHelper.exists(innerException)) {
			portalException.code = undefined;
			portalException.message = innerException.message;
			portalException.originalException = FunctionHelper.applyValue(
				innerException.result?.originalException, innerException
			);
		}

		let webserviceException: WebserviceException | undefined =
			innerException?.result?.exception as WebserviceException;
		if (FunctionHelper.exists(webserviceException)) {
			let faultInfo: FaultInfo = webserviceException.faultInfo
			portalException.code = faultInfo.errorCode;
			portalException.message = faultInfo.errorMessage!;
			portalException.stack = faultInfo.stackTrace;
			portalException.httpStatus = faultInfo.httpStatus;
			portalException.httpStatusText = faultInfo.httpStatusText;
		}

		return portalException;
	}
}